/*
 * D6-SWATCH-NEAR (theme setting perf_card_swatch_near_view). blocks/cx-product-slider.liquid prints
 * the swatch pictures of the cards after a slider's first six (or after the cards one row shows on
 * computers, when that is more) with their style text in data-swatch-style instead of the style
 * attribute, so the browser does not download them with the page, and prints this script after
 * that slider's cards. This script gives every such swatch of a card its style text back,
 * unchanged, once the card (the child of the slider track, .custom-container__cards, that holds
 * it) is within 200 px of the visible part of the track, or once a swatch outside a track is
 * within 200 px of the window. 200 px is the default load distance of the theme's lazy CX
 * containers (lazy_root_margin "200px 0px", blocks/cx-container.liquid), so a card's pictures
 * start as early before it scrolls into view as a lazy row does. A card that is already there
 * when this runs gets its swatches back at the observer's first report.
 *
 * The swatches a card's "+N" button hides sit in overflow-list's overflow slot
 * (snippets/overflow-list.liquid), a hidden part where a background is not downloaded. The page
 * downloads those pictures today with the page, so this script requests them as images when it
 * gives the card its swatches back, and "+N" shows them at once, as today.
 *
 * The distance is not always enough lead: an arrow click scrolls a whole view in a few hundred
 * milliseconds, and on a phone "View more" shows the grid's hidden cards in one step, so a card can
 * reach the view before its picture has arrived. So the first sign that the shopper is about to use
 * a slider (the pointer over it, a press or touch on it, the keyboard focus inside it) gives every
 * card of that slider still waiting its swatches back at once; the pictures then start before the
 * arrow or "View more" acts, as early as the page starts them today for cards it shows.
 *
 * Every slider that needs it prints this script, and a lazy section runs the copy it brings again:
 * the first run sets up, and each later run only looks for cards it has not seen. An observer is
 * disconnected once every card it watches has its swatches back.
 */
(() => {
  const ATTRIBUTE = 'data-swatch-style';
  const MARGIN = '200px';
  const TRACK_SELECTOR = '.custom-container__cards';
  const HIDDEN_SELECTOR = '[slot="overflow"]';
  const SLIDER_SELECTOR = '.cx-product-slider';
  // The shopper's first sign of using a slider: the pointer over it, a press, a touch, the focus.
  const INTENT_EVENTS = ['pointerover', 'pointerdown', 'touchstart', 'focusin'];
  // The picture address in the style text, as in url(//www.tjc.co.uk/cdn/shop/files/a.jpg?v=1&width=80).
  const PICTURE = /url\(\s*['"]?([^'")\s]+)/;

  if (typeof window.tjcSwatchNearViewScan === 'function') {
    window.tjcSwatchNearViewScan();
    return;
  }

  /** @type {WeakSet<Element>} */
  const seen = new WeakSet();
  /** @type {Map<Element | null, { observer: IntersectionObserver, pending: Set<Element> }>} */
  const watchers = new Map();
  // The images requested for hidden swatches, kept for the page's life (one per hidden swatch of a
  // card that came near the view) so the swatch reuses that download when "+N" shows it.
  /** @type {Set<HTMLImageElement>} */
  const pictures = new Set();

  /** @param {Element} target a card, or a swatch outside a track */
  const restore = (target) => {
    const swatches = target.hasAttribute(ATTRIBUTE) ? [target] : target.querySelectorAll(`[${ATTRIBUTE}]`);
    for (const swatch of swatches) {
      const style = swatch.getAttribute(ATTRIBUTE);
      if (style === null) continue;
      swatch.removeAttribute(ATTRIBUTE);
      swatch.setAttribute('style', style);
      const picture = swatch.closest(HIDDEN_SELECTOR) ? PICTURE.exec(style) : null;
      if (!picture) continue;
      const image = new Image();
      image.src = picture[1];
      pictures.add(image);
    }
  };

  /**
   * @param {Element} swatch
   * @param {Element} track
   */
  const cardOf = (swatch, track) => {
    let card = swatch;
    while (card.parentElement && card.parentElement !== track) card = card.parentElement;
    return card;
  };

  /** @type {WeakSet<Element>} */
  const armed = new WeakSet();

  /**
   * Gives every card of the track's slider that still waits its swatches back on the shopper's first
   * sign of using that slider (once per slider).
   * @param {Element} track
   */
  const armSlider = (track) => {
    const slider = track.closest(SLIDER_SELECTOR);
    if (!slider || armed.has(slider)) return;
    armed.add(slider);
    const onIntent = () => {
      for (const type of INTENT_EVENTS) slider.removeEventListener(type, onIntent, true);
      const watcher = watchers.get(track);
      if (!watcher) return;
      for (const card of watcher.pending) restore(card);
      watcher.pending.clear();
      watcher.observer.disconnect();
      watchers.delete(track);
    };
    for (const type of INTENT_EVENTS) slider.addEventListener(type, onIntent, { capture: true, passive: true });
  };

  /** @param {Element | null} track */
  const watcherFor = (track) => {
    let watcher = watchers.get(track);
    if (watcher) return watcher;
    const options = track ? { root: track, rootMargin: `0px ${MARGIN}` } : { rootMargin: MARGIN };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // One report can hold several entries for a card (in, out, in): only the first one in counts.
        if (!entry.isIntersecting || !watcher.pending.delete(entry.target)) continue;
        observer.unobserve(entry.target);
        restore(entry.target);
      }
      if (watcher.pending.size > 0) return;
      observer.disconnect();
      watchers.delete(track);
    }, options);
    watcher = { observer, pending: new Set() };
    watchers.set(track, watcher);
    return watcher;
  };

  const scan = () => {
    for (const swatch of document.querySelectorAll(`[${ATTRIBUTE}]`)) {
      const track = swatch.closest(TRACK_SELECTOR);
      const target = track ? cardOf(swatch, track) : swatch;
      if (seen.has(target)) continue;
      seen.add(target);
      if (typeof IntersectionObserver !== 'function') {
        restore(target);
        continue;
      }
      const watcher = watcherFor(track);
      watcher.pending.add(target);
      watcher.observer.observe(target);
      if (track) armSlider(track);
    }
  };

  window.tjcSwatchNearViewScan = scan;
  scan();
})();
